import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { find } from "lodash";
import { breakpoint, theme } from "@utilities";
import { Z_INDEX } from "@constants";

interface DropdownProps {
    options: { label: string; value: string }[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

export const Dropdown = ({
    options,
    value,
    onChange,
    placeholder = "Select an option",
    disabled = false,
}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleSelect = (value: string) => {
        if (!disabled) {
            onChange(value);
            setIsOpen(false);
        }
    };

    const selectedLabel = (() => {
        if (typeof value === "string") {
            const hasMatchingOption = find(options, { value });
            return hasMatchingOption && hasMatchingOption.label;
        }
    })();

    return (
        <DropdownWrapper ref={dropdownRef}>
            <DropdownHeader onClick={toggleDropdown} disabled={disabled}>
                <LabelContainer>
                    <LabelPlaceHolder hasValue={!!selectedLabel}>{placeholder}</LabelPlaceHolder>
                    <LabelText data-fulltext={selectedLabel || ""}>{selectedLabel}</LabelText>
                </LabelContainer>
                <StyledArrow $isOpen={isOpen} />
            </DropdownHeader>
            {isOpen && !disabled && (
                <DropdownList>
                    {options.map((option) => (
                        <DropdownItem
                            key={option.value}
                            title={option.label}
                            onClick={() => handleSelect(option.value)}
                        >
                            {option.label}
                        </DropdownItem>
                    ))}
                </DropdownList>
            )}
        </DropdownWrapper>
    );
};

// Styled Components
const DropdownWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const StyledArrow = styled(KeyboardArrowDownIcon)<{ $isOpen: boolean }>`
    transition: transform 0.5s;
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
    color: ${theme.colors.highlight};
`;

const DropdownHeader = styled.div<{ disabled: boolean }>`
    display: flex;
    border-radius: 8px;
    align-items: center;
    justify-content: space-between;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    padding: 8px 20px;
    border: 1px solid ${({ disabled }) => (disabled ? "#ccc" : theme.colors.highlight)};
    background-color: ${({ disabled }) => (disabled ? "#f5f5f5" : "#fff")};
    color: ${({ disabled }) => (disabled ? "#aaa" : "#000")};
`;

const DropdownList = styled.ul`
    position: absolute;
    border-radius: 8px;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #ccc;
    max-height: 300px;
    overflow-y: auto;
    z-index: ${Z_INDEX.DROPDOWN};
`;

const LabelContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const LabelText = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    flex: 1;
    min-width: 0;
    &:hover::after {
        content: attr(data-fulltext);
        position: absolute;
        top: 100%;
        left: 0;
        background: #333;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        white-space: normal;
        z-index: ${Z_INDEX.TOOLTIP};
        max-width: 300px;
    }

    ${breakpoint.breakLarge`
        max-width: 650px;
    `}
`;
const LabelPlaceHolder = styled.span<{ hasValue: boolean }>`
    color: #6e7375;
    font-size: ${({ hasValue }) => (hasValue ? "12px" : "16px")};
    margin-bottom: ${({ hasValue }) => (hasValue ? "6px" : "0px")};
    transition: font-size 0.2s;
`;

const DropdownItem = styled.li`
    padding: 10px;
    cursor: pointer;
    &:hover {
        background: #f5f5f5;
    }
`;
